import PropTypes from 'prop-types';
import React from 'react';

import Popup from "reactjs-popup";
import { AlertTriangle, Badge, Button } from "@opendate-app/setlist";

import "../styles/offersTab.css";
import { lineupLabel } from "../../../shared/offerLineupPerformer";

// Presentational offers view (O-3702) shared by the legacy ConfirmShow
// ManageOffers section and the new Event Page OffersTab. Fully driven by
// normalized `rows` + per-row callbacks, so each page wires its own data layer
// (Redux thunks on the legacy page, axios on the new page) without duplicating
// the table/cards/menu markup.

// reactjs-popup renders content in a body portal that defaults to a white
// background; pull it from the --sl-* tokens so the menu/confirm go dark on
// themed pages (and stay white in light mode).
const popupContentStyle = {
  "border": "1px solid var(--sl-border-secondary)",
  "borderRadius": "8px",
  "boxShadow": "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  "width": "250px",
  "padding": "0px",
  "background": "var(--sl-bg-primary)"
};

const deleteModalContentStyle = {
  "textAlign": "left",
  "border": "1px solid var(--sl-border-secondary)",
  "borderRadius": "8px",
  "boxShadow": "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  "width": "500px",
  "padding": "15px",
  "background": "var(--sl-bg-primary)"
};

const STATUS_LABELS = {
  draft: 'Draft', sent: 'Sent', accepted: 'Accepted', declined: 'Declined'
};

const statusBadge = (state) => (
  <span className={`sl-offer-status sl-offer-status--${state}`}>
    {STATUS_LABELS[state] || state}
  </span>
);

const offerMenu = (row) => (
  <Popup
    arrow={false}
    offsetY={5}
    position="bottom right"
    contentStyle={popupContentStyle}
    onOpen={(e) => { e.stopPropagation(); e.preventDefault(); }}
    trigger={() => (
      <a href="#" className="sl-offer-menu-trigger" onClick={(e) => e.preventDefault()}>
        <i className="fas fa-ellipsis-v"></i>
      </a>
    )}
  >
    {close => (
      <ul className="sl-offer-menu">
        {row.state !== "accepted" && row.state !== "declined" ? (
          <li className="sl-offer-menu-item">
            <a href="#" onClick={(e) => { e.preventDefault(); close(); row.onAccept(); }}>
              <span className="sl-offer-menu-item-icon"><i className="fas fa-check-circle"></i></span>
              <span>Mark as Accepted</span>
            </a>
          </li>
        ) : null}
        {row.state !== "accepted" && row.state !== "declined" ? (
          <li className="sl-offer-menu-item">
            <a href="#" onClick={(e) => { e.preventDefault(); close(); row.onDecline(); }}>
              <span className="sl-offer-menu-item-icon"><i className="fas fa-times-circle"></i></span>
              <span>Mark as Declined</span>
            </a>
          </li>
        ) : null}
        <li className="sl-offer-menu-item">
          <a href={row.downloadHref} target="_blank" rel="noreferrer">
            <span className="sl-offer-menu-item-icon"><i className="fas fa-download"></i></span>
            <span>Download PDF</span>
          </a>
        </li>
        <li className="sl-offer-menu-item">
          <a href="#" onClick={(e) => { e.preventDefault(); close(); row.onPreview(); }}>
            <span className="sl-offer-menu-item-icon"><i className="fas fa-search"></i></span>
            <span>Preview</span>
          </a>
        </li>
        <li className="sl-offer-menu-item">
          <a href="#" onClick={(e) => { e.preventDefault(); close(); row.onEdit(); }}>
            <span className="sl-offer-menu-item-icon"><i className="fas fa-pencil"></i></span>
            <span>Edit</span>
          </a>
        </li>
        <li className="sl-offer-menu-item sl-offer-menu-item--danger">
          <Popup
            trigger={(
              <a href="#">
                <span className="sl-offer-menu-item-icon"><i className="fas fa-trash"></i></span>
                <span>Delete</span>
              </a>
            )}
            modal
            contentStyle={deleteModalContentStyle}
            onOpen={(e) => { e.preventDefault(); }}
            closeOnDocumentClick
          >
            {closeDelete => (
              <div className="sl-offer-delete">
                <div className="sl-offer-delete-head">
                  <h5><strong>Delete offer?</strong></h5>
                  <a
                    href="#"
                    className="sl-offer-delete-close"
                    onClick={(e) => { e.preventDefault(); closeDelete(); }}
                  >
                    <i className="fa fa-times"></i>
                  </a>
                </div>
                <p>You're about to delete this offer. This cannot be undone.</p>
                <div className="sl-offer-delete-actions">
                  <a href="#" onClick={(e) => { e.preventDefault(); closeDelete(); }}>
                    Cancel
                  </a>
                  <a
                    href="#"
                    className="sl-btn-danger ml-3"
                    onClick={(e) => {
                      e.preventDefault();
                      closeDelete();
                      close();
                      row.onDelete();
                    }}
                  >
                    Yes, delete offer
                  </a>
                </div>
              </div>
            )}
          </Popup>
        </li>
      </ul>
    )}
  </Popup>
);

const noArtistBadge = (label) => (
  <Badge type="pill-color" color="warning" size="sm" icon={AlertTriangle}>{label}</Badge>
);

const artistCell = (row) => (
  <>
    <span className="sl-offer-artist">{row.name}</span>
    {row.isV3 ? <span className="sl-offer-v3-badge">V3</span> : null}
    {row.artistExtra ? (
      <span className="sl-offer-attach-wrap" onClick={(e) => e.stopPropagation()}>
        {row.artistExtra}
      </span>
    ) : null}
    {row.missingPerformer ? (
      <div className="sl-offer-no-artist-line" onClick={(e) => e.stopPropagation()}>
        {noArtistBadge("No artist")}
        {row.missingPerformer.onAttach ? row.missingPerformer.performers.map((performer) => (
          <Button
            key={performer.id}
            variant="link-color"
            size="sm"
            onClick={() => row.missingPerformer.onAttach(performer)}
          >
            Attach {performer.artistable?.name}
          </Button>
        )) : null}
      </div>
    ) : null}
  </>
);

const cardLineupStrip = (row) => (
  row.missingPerformer?.onAttach && row.missingPerformer.performers.length > 0 ? (
    <div className="sl-offer-card-lineup" onClick={(e) => e.stopPropagation()}>
      {row.missingPerformer.performers.map((performer) => (
        <div key={performer.id} className="sl-offer-card-lineup-item">
          <span>On this event: <strong>{lineupLabel(performer)}</strong></span>
          <Button variant="primary" size="sm" onClick={() => row.missingPerformer.onAttach(performer)}>
            Attach
          </Button>
        </div>
      ))}
    </div>
  ) : null
);

const OffersListView = ({
  rows,
  view,
  onSelectView,
  canManage,
  onNewOffer,
  locked,
  headerActions,
  footer,
}) => {
  const hasRows = rows.length > 0;
  const needsArtistCount = rows.filter((row) => row.missingPerformer).length;

  const tableView = (
    <div className="sl-offers-table-wrap">
      <table className="sl-offers-table">
        <thead>
          <tr>
            <th>Artist</th>
            <th>Type</th>
            <th>Guarantee</th>
            <th>Backend</th>
            <th>Status</th>
            <th>Created</th>
            {canManage ? <th className="sl-offers-table-actions" aria-label="Actions"></th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.key}
              className={`sl-offer-row ${row.missingPerformer ? "sl-offer-row--needs-artist" : ""}`.trim()}
              onClick={() => (row.onRowClick || row.onPreview)()}>
              <td>{artistCell(row)}</td>
              <td>{row.type}</td>
              <td>{row.guarantee}</td>
              <td>{row.backend}</td>
              <td>{statusBadge(row.state)}</td>
              <td className="sl-offer-created">{row.created}</td>
              {canManage ? (
                <td className="sl-offers-table-actions" onClick={(e) => e.stopPropagation()}>
                  {offerMenu(row)}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const cardsView = (
    <div className="sl-offers-grid">
      {rows.map((row) => (
        <div
          key={row.key}
          className={`sl-offer-card ${row.missingPerformer ? "sl-offer-card--needs-artist" : ""}`.trim()}
          onClick={() => (row.onRowClick || row.onPreview)()}>
          <div className="sl-offer-card-head">
            <div className="sl-offer-card-head-main">
              <div className="sl-offer-card-label">
                OFFER FOR {row.isV3 ? <span className="sl-offer-v3-badge">V3</span> : null}
              </div>
              <div className="sl-offer-artist">
                {row.name}
                {row.artistExtra ? (
                  <span className="sl-offer-attach-wrap" onClick={(e) => e.stopPropagation()}>
                    {row.artistExtra}
                  </span>
                ) : null}
              </div>
              {row.missingPerformer ? (
                <div className="sl-offer-card-no-artist">{noArtistBadge("No artist attached")}</div>
              ) : null}
            </div>
            <div className="sl-offer-card-head-aside" onClick={(e) => e.stopPropagation()}>
              {statusBadge(row.state)}
              {canManage ? offerMenu(row) : null}
            </div>
          </div>
          <div className="sl-offer-card-meta">
            <div><span className="sl-offer-meta-label">Type</span><span>{row.type}</span></div>
            <div><span className="sl-offer-meta-label">Guarantee</span><span>{row.guarantee}</span></div>
            <div><span className="sl-offer-meta-label">Backend</span><span>{row.backend}</span></div>
            <div><span className="sl-offer-meta-label">Created</span><span>{row.created}</span></div>
          </div>
          {cardLineupStrip(row)}
        </div>
      ))}
    </div>
  );

  return (
    <div className="sl-offers">
      <div className="sl-offers-header">
        <h5 className="sl-offers-title">
          Offers
          {hasRows ? <span className="sl-offers-count">{rows.length}</span> : null}
          {locked ? <i className="ml-2 far fa-lock small sl-offer-lock"></i> : null}
          {needsArtistCount > 0 ? (
            <span className="sl-offers-needs-artist">
              <Badge type="pill-color" color="warning" size="sm">
                {needsArtistCount} {needsArtistCount === 1 ? "needs" : "need"} an artist
              </Badge>
            </span>
          ) : null}
        </h5>
        <div className="sl-offers-header-actions">
          {hasRows ? (
            <div className="sl-offers-view-toggle" role="group" aria-label="Offers view">
              <button
                type="button"
                className={`sl-offers-view-btn ${view === "table" ? "active" : ""}`.trim()}
                onClick={() => onSelectView("table")}
                title="Table view"
                aria-label="Table view"
              >
                <i className="fas fa-list"></i>
              </button>
              <button
                type="button"
                className={`sl-offers-view-btn ${view === "cards" ? "active" : ""}`.trim()}
                onClick={() => onSelectView("cards")}
                title="Card view"
                aria-label="Card view"
              >
                <i className="fas fa-th-large"></i>
              </button>
            </div>
          ) : null}
          {canManage && hasRows ? (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); onNewOffer(); }}
              className="sl-btn-secondary"
            >
              <strong>New Offer</strong>
            </a>
          ) : null}
          {headerActions}
        </div>
      </div>

      {!hasRows && !footer ? (
        <div className="sl-offers-empty">
          <div className="sl-offers-empty-icon">
            <i className="fal fa-file-contract"></i>
          </div>
          <div className="sl-offers-empty-body">
            <h5>Create an Offer</h5>
            <p className="sl-offers-empty-text">
              Now that this event is confirmed, let's get an offer on the books.
            </p>
            {canManage ? (
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); onNewOffer(); }}
                className="sl-btn-primary"
              >
                <strong>Create Offer</strong>
              </a>
            ) : null}
          </div>
        </div>
      ) : null}

      {hasRows ? (view === "cards" ? cardsView : tableView) : null}

      {footer}
    </div>
  );
};

const rowShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  name: PropTypes.string,
  isV3: PropTypes.bool,
  artistExtra: PropTypes.node,
  missingPerformer: PropTypes.shape({
    performers: PropTypes.arrayOf(PropTypes.object).isRequired,
    onAttach: PropTypes.func,
  }),
  state: PropTypes.string,
  type: PropTypes.string,
  guarantee: PropTypes.string,
  backend: PropTypes.string,
  created: PropTypes.string,
  onAccept: PropTypes.func,
  onDecline: PropTypes.func,
  onDelete: PropTypes.func,
  onPreview: PropTypes.func,
  onRowClick: PropTypes.func,
  onEdit: PropTypes.func,
  downloadHref: PropTypes.string,
});

OffersListView.propTypes = {
  rows: PropTypes.arrayOf(rowShape).isRequired,
  view: PropTypes.oneOf(["table", "cards"]).isRequired,
  onSelectView: PropTypes.func.isRequired,
  canManage: PropTypes.bool,
  onNewOffer: PropTypes.func.isRequired,
  locked: PropTypes.bool,
  headerActions: PropTypes.node,
  footer: PropTypes.node,
};

export default OffersListView;
